import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Radio, Wifi, WifiOff, RefreshCw, Power, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Add keyframes for spin animation
const spinKeyframes = `
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = spinKeyframes;
  document.head.appendChild(styleEl);
}

export default function AdminWhatsApp() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);
  const abortControllerRef = useRef(null);
  const isFetchingRef = useRef(false);

  const fetchSessions = useCallback(async (isManualRefresh = false) => {
    // Prevent concurrent fetches
    if (isFetchingRef.current && !isManualRefresh) return;
    
    // Cancel previous request if manual refresh
    if (abortControllerRef.current && isManualRefresh) {
      abortControllerRef.current.abort();
    }
    
    isFetchingRef.current = true;
    if (isManualRefresh) setRefreshing(true);
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await axios.get(`${API}/admin/whatsapp/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortControllerRef.current.signal
      });
      
      // Use functional update to ensure we're replacing, not merging
      const newSessions = response.data.sessions || [];
      setSessions(newSessions);
    } catch (error) {
      if (axios.isCancel(error)) {
        // Request was cancelled, ignore
        return;
      }
      console.error('Error fetching sessions:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('admin_token');
        window.location.href = '/admin/login';
      }
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(() => fetchSessions(false), 5000); // Back to 5 seconds
    return () => {
      clearInterval(interval);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchSessions]);

  const handleManualRefresh = () => {
    fetchSessions(true);
  };

  const handleDisconnect = async (userId, sessionId) => {
    const confirmed = window.confirm('Are you sure you want to disconnect this user\'s WhatsApp?');
    if (!confirmed) return;

    const disconnectKey = `${userId}:${sessionId}`;
    setDisconnecting(disconnectKey);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/whatsapp/disconnect/${userId}`, { session_id: sessionId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTimeout(() => {
        fetchSessions();
        setDisconnecting(null);
      }, 2000);
    } catch (error) {
      console.error('Error disconnecting:', error);
      alert(error.response?.data?.detail || 'Failed to disconnect');
      setDisconnecting(null);
    }
  };

  const formatPhoneNumber = (num) => {
    if (!num) return 'N/A';
    if (num.startsWith('91') && num.length >= 12) {
      return `+${num.slice(0,2)} ${num.slice(2,7)} ${num.slice(7)}`;
    }
    return `+${num}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      connected: { bg: '#dcfce7', color: '#166534', text: 'Connected' },
      qr_ready: { bg: '#fef3c7', color: '#92400e', text: 'Waiting for QR Scan' },
      qr_pending: { bg: '#fef3c7', color: '#92400e', text: 'QR Pending' },
      initializing: { bg: '#dbeafe', color: '#1e40af', text: 'Initializing' },
      disconnected: { bg: '#fee2e2', color: '#991b1b', text: 'Disconnected' }
    };
    const config = statusConfig[status] || { bg: '#f1f5f9', color: '#475569', text: status };
    return config;
  };

  // Show all sessions, not just connected ones
  const activeSessions = sessions.filter(s => ['connected', 'qr_ready', 'qr_pending', 'initializing'].includes(s.status));
  const connectedSessions = sessions.filter(s => s.connected);

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', color: '#64748b' }}>
          <div>Loading WhatsApp sessions...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>WhatsApp Sessions</h1>
          <p style={{ color: '#64748b', fontSize: '15px' }}>
            Monitor and manage user WhatsApp connections
            <span style={{ marginLeft: '12px', fontSize: '12px', color: '#94a3b8' }}>
              🔄 Auto-refreshing every 5s
            </span>
          </p>
        </div>
        <button
          onClick={handleManualRefresh}
          disabled={refreshing}
          style={{ 
            padding: '10px 20px', 
            background: refreshing ? '#94a3b8' : '#667eea', 
            border: 'none', 
            borderRadius: '8px', 
            color: 'white', 
            cursor: refreshing ? 'not-allowed' : 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            opacity: refreshing ? 0.7 : 1
          }}
        >
          <RefreshCw size={18} className={refreshing ? 'spin' : ''} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Summary Card */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #10b981', maxWidth: '280px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '4px' }}>Active Connections</p>
              <h3 style={{ color: '#1e293b', fontSize: '32px', fontWeight: '700', margin: 0 }}>{connectedSessions.length}</h3>
            </div>
            <Wifi size={32} style={{ color: '#10b981' }} />
          </div>
        </div>
      </div>

      {/* Sessions List */}
      <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0' }}>
          <h3 style={{ color: '#1e293b', fontSize: '16px', fontWeight: '600', margin: 0 }}>All WhatsApp Sessions</h3>
        </div>
        
        {activeSessions.length > 0 ? (
          <div>
            {activeSessions.map((session, index) => {
              const statusBadge = getStatusBadge(session.status);
              // Use sessionKey as unique identifier (userId:sessionId)
              const uniqueKey = session.sessionKey || `${session.userId}-${session.sessionId}-${index}`;
              return (
              <div
                key={uniqueKey}
                style={{
                  padding: '20px',
                  borderBottom: index < activeSessions.length - 1 ? '1px solid #e2e8f0' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: session.connected ? '#dcfce7' : '#fef3c7',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {session.connected ? <Wifi size={24} style={{ color: '#10b981' }} /> : <WifiOff size={24} style={{ color: '#f59e0b' }} />}
                  </div>
                  <div>
                    <p style={{ color: '#1e293b', fontWeight: '600', margin: '0 0 4px 0' }}>
                      {session.userEmail || 'Unknown User'}
                      {session.sessionId && (
                        <span style={{ marginLeft: '8px', fontSize: '11px', color: '#94a3b8', fontWeight: '400' }}>
                          (Session: {session.sessionId})
                        </span>
                      )}
                    </p>
                    <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>
                      📱 {session.connected ? formatPhoneNumber(session.phoneNumber) : 'Not connected yet'}
                      {session.workerId && <span style={{ marginLeft: '12px', fontSize: '11px', color: '#94a3b8' }}>• {session.workerId}</span>}
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    padding: '4px 12px',
                    background: statusBadge.bg,
                    color: statusBadge.color,
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {statusBadge.text}
                  </span>
                  {session.connected && (
                  <button
                    onClick={() => handleDisconnect(session.userId, session.sessionId)}
                    disabled={disconnecting === `${session.userId}:${session.sessionId}`}
                    style={{
                      padding: '8px 16px',
                      background: '#fee2e2',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      opacity: disconnecting === `${session.userId}:${session.sessionId}` ? 0.6 : 1
                    }}
                  >
                    <Power size={14} />
                    {disconnecting === `${session.userId}:${session.sessionId}` ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
            <WifiOff size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
            <p>No active WhatsApp sessions</p>
            <p style={{ fontSize: '13px', marginTop: '8px' }}>Users can connect their WhatsApp from their dashboard</p>
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginTop: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ color: '#1e293b', fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Radio size={18} />
          Per-User WhatsApp Connections
        </h3>
        <ul style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.8', margin: 0, paddingLeft: '20px' }}>
          <li>Each user can connect their own WhatsApp account</li>
          <li>Users connect by scanning QR code from their dashboard</li>
          <li>Messages are sent from the user's connected WhatsApp number</li>
          <li>Disconnecting a user will require them to scan QR again</li>
        </ul>
      </div>

      {/* Auto-refresh */}
      <div style={{ marginTop: '24px', textAlign: 'center' }}>
        <p style={{ color: '#94a3b8', fontSize: '13px' }}>
          <RefreshCw size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
          Auto-refreshing every 5 seconds
        </p>
      </div>
    </AdminLayout>
  );
}
